import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useHealthCheck } from '../hooks/useHealthCheck';
import { useResendTimer } from '../hooks/useResendTimer';
import { logger } from '../lib/utils/logger';
import type { SignupConfig } from '../lib/services/auth';
import { authService } from '../lib/services/auth';
import { ThemedCard } from '../components/themed';
import { SSOProviders } from '../components/auth/SSOProviders';
import { Mail, Lock, AlertCircle, Loader2, CheckCircle, RefreshCw, ArrowLeft } from 'lucide-react';
import { useFormSubmission } from '../hooks/useFormSubmission';
import SignupDisabled from './SignupDisabled';
import { hasStatusCode, isApiError } from '../lib/utils/errorHandling';
import { APP_ROUTES, marketingUrl } from '../config/routes';
import ThemeToggle from '../components/ThemeToggle';

type SignupState = { phase: 'form' } | { phase: 'awaiting-verification'; email: string };

/**
 * Signup page component for self-service user registration.
 *
 * Two-state machine:
 * 1. `form` — collect email + password + terms acceptance
 * 2. `awaiting-verification` — inline 6-digit OTP entry with resend cooldown
 *
 * After successful OTP verification, the user is logged in and routed via
 * `/welcome` (PostLoginRouter then directs them into the onboarding wizard).
 *
 * Profile (first/last name) and organization join code are collected in the
 * onboarding wizard, not on this form.
 */
const Signup: React.FC = () => {
  const [signupConfig, setSignupConfig] = useState<SignupConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [signupState, setSignupState] = useState<SignupState>({ phase: 'form' });

  const [searchParams] = useSearchParams();

  // Form state (phase 1). Pre-fill the email when arriving from an invitation
  // (/accept-invitation → /signup?invite_token=…&email=…) so the new invitee
  // registers with the invited address and lands in the accept branch, not the
  // wrong-user branch.
  const [email, setEmail] = useState(() => searchParams.get('email') ?? '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [passwordMismatch, setPasswordMismatch] = useState(false);

  // OTP state (phase 2)
  const [otpCode, setOtpCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);

  const navigate = useNavigate();
  const healthStatus = useHealthCheck();
  const { canResend, secondsRemaining, startTimer, reset: resetResendTimer } = useResendTimer(30);

  // Fetch signup configuration on mount
  useEffect(() => {
    const fetchSignupConfig = async () => {
      try {
        const config = await authService.getSignupConfig();
        setSignupConfig(config);
        logger.info('Signup config loaded:', config);
      } catch (err) {
        logger.error('Failed to load signup config:', err);
        // Assume disabled if can't fetch config
        setSignupConfig({
          enabled: false,
          mode: 'invitation_only',
          allowedDomains: [],
          message: 'Unable to load signup configuration. Please try again later.',
        });
      } finally {
        setConfigLoading(false);
      }
    };

    fetchSignupConfig();
  }, []);

  const { isSubmitting, handleSubmit, getFieldProps } = useFormSubmission({
    onSubmit: async () => {
      setError(null);
      setPasswordMismatch(false);

      // Validate passwords match
      if (password !== confirmPassword) {
        setPasswordMismatch(true);
        setError('Passwords do not match');
        return;
      }

      // Validate terms accepted
      if (!termsAccepted) {
        setError('You must accept the Terms of Service to sign up');
        return;
      }

      const response = await authService.signup({
        email,
        password,
        termsAccepted,
      });

      logger.info('Signup successful, awaiting verification', {
        email: response.email,
      });

      // Transition to phase 2 — same page, swap content
      setSignupState({ phase: 'awaiting-verification', email: response.email });
      setOtpCode('');
      setVerifyError(null);
      setResendError(null);
      setResendSuccess(false);
      resetResendTimer();
    },
    onError: (err) => {
      logger.error('Signup error:', err);

      if (hasStatusCode(err, 409)) {
        setError('An account with this email already exists. Please log in instead.');
      } else if (hasStatusCode(err, 422)) {
        if (isApiError(err) && err.details !== undefined) {
          const detail = err.details;
          if (typeof detail === 'string') {
            setError(detail);
          } else if (Array.isArray(detail)) {
            setError(
              detail
                .map((e) =>
                  typeof e === 'object' && e !== null && ('message' in e || 'msg' in e)
                    ? (e as { message?: string; msg?: string }).message ||
                      (e as { message?: string; msg?: string }).msg ||
                      ''
                    : '',
                )
                .filter(Boolean)
                .join(', '),
            );
          } else {
            setError('Invalid signup information. Please check your inputs.');
          }
        } else {
          setError('Invalid signup information. Please check your inputs.');
        }
      } else if (hasStatusCode(err, 403)) {
        setError(
          'Signup is currently disabled. Please contact an administrator for an invitation.',
        );
      } else {
        setError('Unable to create account. Please try again later.');
      }
    },
  });

  // Real-time password match validation
  const handleConfirmPasswordChange = (value: string) => {
    setConfirmPassword(value);
    if (password && value && password !== value) {
      setPasswordMismatch(true);
    } else {
      setPasswordMismatch(false);
    }
  };

  const performVerification = async (code: string, verificationEmail: string) => {
    setVerifying(true);
    setVerifyError(null);

    try {
      if (!/^\d{6}$/.test(code)) {
        setVerifyError('Please enter a valid 6-digit code');
        setVerifying(false);
        return;
      }

      logger.info('Verifying OTP code');
      await authService.verifyOtp(verificationEmail, code);
      logger.info('OTP verified successfully');

      // If the user arrived to accept an invitation, return them to the accept
      // page (now verified + authenticated) so they land in the one-click
      // Accept branch instead of the generic onboarding route.
      const inviteToken = searchParams.get('invite_token');
      if (inviteToken !== null && inviteToken !== '') {
        navigate(`${APP_ROUTES.ACCEPT_INVITATION}?token=${encodeURIComponent(inviteToken)}`, {
          replace: true,
        });
        return;
      }

      navigate(APP_ROUTES.WELCOME, { replace: true });
    } catch (err) {
      logger.error('Failed to verify OTP:', err);
      setVerifyError('Invalid or expired code. Check the code or click the link in your email.');
      setVerifying(false);
    }
  };

  const handleOtpVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (signupState.phase !== 'awaiting-verification') return;
    await performVerification(otpCode, signupState.email);
  };

  const handleOtpChange = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 6);
    setOtpCode(digits);

    // Auto-submit when 6 digits are entered
    if (digits.length === 6 && signupState.phase === 'awaiting-verification' && !verifying) {
      void performVerification(digits, signupState.email);
    }
  };

  const handleResend = async () => {
    if (signupState.phase !== 'awaiting-verification' || !canResend) return;

    setResending(true);
    setResendError(null);
    setResendSuccess(false);

    try {
      await authService.resendVerificationEmail(signupState.email);
      setResendSuccess(true);
      startTimer();
      logger.info('Verification email resent successfully');
    } catch (err) {
      logger.error('Failed to resend verification email:', err);
      setResendError('Failed to resend email. Please try again.');
    } finally {
      setResending(false);
    }
  };

  const handleUseDifferentEmail = () => {
    setSignupState({ phase: 'form' });
    setOtpCode('');
    setVerifyError(null);
    setResendError(null);
    setResendSuccess(false);
    resetResendTimer();
  };

  // Show loading state while fetching config
  if (configLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center surface-secondary relative">
        <div className="absolute top-4 right-4">
          <ThemeToggle />
        </div>
        <div className="w-96 flex flex-col items-center gap-6">
          <div className="flex flex-col items-center gap-3">
            <div className="flex items-center gap-2">
              <img src="/coldsea-logo.svg" alt="" className="h-7 dark:invert" aria-hidden="true" />
              <h1 className="text-3xl font-bold text-primary">Deploy</h1>
            </div>
          </div>

          <ThemedCard className="w-full">
            <div className="p-6 flex flex-col items-center">
              <Loader2 className="h-8 w-8 animate-spin text-sky-600 dark:text-sky-400 mb-4" />
              <p className="text-sm text-secondary">Loading signup configuration...</p>
            </div>
          </ThemedCard>
        </div>
      </div>
    );
  }

  // Show restricted access page if signup is disabled or invitation-only
  // Note: whitelist mode SHOWS the form but validates domain on submit
  if (signupConfig && !signupConfig.enabled) {
    return <SignupDisabled mode="disabled" allowedDomains={signupConfig.allowedDomains} />;
  }

  if (signupConfig && signupConfig.mode === 'invitation_only') {
    return <SignupDisabled mode="invitation_only" allowedDomains={signupConfig.allowedDomains} />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center surface-secondary relative">
      <div className="absolute top-4 right-4">
        <ThemeToggle />
      </div>
      <div className="w-96 flex flex-col items-center gap-6">
        {/* Branding */}
        <div className="flex flex-col items-center gap-3">
          <div className="flex items-center gap-2">
            <img src="/coldsea-logo.svg" alt="" className="h-7 dark:invert" aria-hidden="true" />
            <h1 className="text-3xl font-bold text-primary">Deploy</h1>
          </div>
        </div>

        <ThemedCard className="w-full">
          {signupState.phase === 'form' ? (
            <SignupForm
              signupConfig={signupConfig}
              email={email}
              setEmail={setEmail}
              password={password}
              setPassword={setPassword}
              confirmPassword={confirmPassword}
              onConfirmPasswordChange={handleConfirmPasswordChange}
              termsAccepted={termsAccepted}
              setTermsAccepted={setTermsAccepted}
              passwordMismatch={passwordMismatch}
              error={error}
              isHealthy={healthStatus.isHealthy}
              isSubmitting={isSubmitting}
              onSubmit={handleSubmit}
              getFieldProps={getFieldProps}
            />
          ) : (
            <AwaitingVerification
              email={signupState.email}
              otpCode={otpCode}
              onOtpChange={handleOtpChange}
              verifying={verifying}
              verifyError={verifyError}
              resending={resending}
              resendSuccess={resendSuccess}
              resendError={resendError}
              canResend={canResend}
              secondsRemaining={secondsRemaining}
              onSubmit={handleOtpVerify}
              onResend={handleResend}
              onUseDifferentEmail={handleUseDifferentEmail}
            />
          )}
        </ThemedCard>
      </div>
    </div>
  );
};

interface SignupFormProps {
  signupConfig: SignupConfig | null;
  email: string;
  setEmail: (value: string) => void;
  password: string;
  setPassword: (value: string) => void;
  confirmPassword: string;
  onConfirmPasswordChange: (value: string) => void;
  termsAccepted: boolean;
  setTermsAccepted: (value: boolean) => void;
  passwordMismatch: boolean;
  error: string | null;
  isHealthy: boolean;
  isSubmitting: boolean;
  onSubmit: (e: React.FormEvent) => void;
  getFieldProps: () => Record<string, unknown>;
}

function SignupForm({
  signupConfig,
  email,
  setEmail,
  password,
  setPassword,
  confirmPassword,
  onConfirmPasswordChange,
  termsAccepted,
  setTermsAccepted,
  passwordMismatch,
  error,
  isHealthy,
  isSubmitting,
  onSubmit,
  getFieldProps,
}: SignupFormProps) {
  return (
    <form onSubmit={onSubmit} className="flex flex-col" data-testid="signup-form">
      <div className="p-6">
        <h3 className="text-xl font-semibold text-center mb-2 text-primary">Create your account</h3>
        <p className="text-sm text-center text-secondary mb-6">
          Get started with Coldsea Deploy in minutes
        </p>

        <SSOProviders mode="signup" />

        {/* Whitelist mode notice */}
        {signupConfig !== null &&
          signupConfig.mode === 'whitelist' &&
          signupConfig.allowedDomains.length > 0 && (
            <div className="mb-4 p-3 bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800 rounded-lg">
              <div className="flex gap-2">
                <CheckCircle className="h-5 w-5 text-sky-600 dark:text-sky-400 shrink-0 mt-0.5" />
                <div className="text-sm text-sky-800 dark:text-sky-200">
                  <p className="font-medium mb-1">Beta access</p>
                  <p className="text-sky-700 dark:text-sky-300">
                    Approved domains: {signupConfig.allowedDomains.map((d) => `@${d}`).join(', ')}
                  </p>
                </div>
              </div>
            </div>
          )}

        {!isHealthy && (
          <div className="mb-4 p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg flex gap-2">
            <AlertCircle className="h-5 w-5 text-orange-700 dark:text-orange-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-base font-medium text-orange-800 dark:text-orange-200">
                System Unavailable
              </p>
              <p className="text-sm mt-1 text-orange-700 dark:text-orange-300">
                The system is currently unavailable. Please try again later.
              </p>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        <div className="space-y-4">
          {/* Email Field */}
          <div>
            <label htmlFor="email" className="form-label">
              Email Address
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail className="h-5 w-5 text-zinc-400 dark:text-zinc-500" />
              </div>
              <input
                id="email"
                type="email"
                name="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="form-input pl-11!"
                placeholder="you@example.com"
                data-testid="signup-email-input"
                {...getFieldProps()}
              />
            </div>
          </div>

          {/* Password Field */}
          <div>
            <label htmlFor="password" className="form-label">
              Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-zinc-400 dark:text-zinc-500" />
              </div>
              <input
                id="password"
                type="password"
                name="new-password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                className="form-input pl-11!"
                placeholder="Minimum 8 characters"
                data-testid="signup-password-input"
                {...getFieldProps()}
              />
            </div>
          </div>

          {/* Confirm Password Field */}
          <div>
            <label htmlFor="confirmPassword" className="form-label">
              Confirm Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-zinc-400 dark:text-zinc-500" />
              </div>
              <input
                id="confirmPassword"
                type="password"
                name="confirm-password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => onConfirmPasswordChange(e.target.value)}
                required
                minLength={8}
                className={`form-input pl-11! ${
                  passwordMismatch ? 'border-red-300 dark:border-red-700 focus:ring-red-500' : ''
                }`}
                placeholder="Re-enter your password"
                data-testid="signup-confirm-password-input"
                {...getFieldProps()}
              />
              {confirmPassword && !passwordMismatch && password === confirmPassword && (
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                </div>
              )}
            </div>
            {passwordMismatch && confirmPassword && (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400">Passwords must match</p>
            )}
          </div>

          {/* Terms of Service Checkbox */}
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="terms"
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="w-4 h-4 border border-zinc-300 dark:border-zinc-600 rounded-sm bg-white dark:bg-zinc-700 focus:ring-2 focus:ring-sky-500"
                required
                data-testid="signup-terms-checkbox"
              />
            </div>
            <div className="ml-2 text-sm">
              <label htmlFor="terms" className="text-zinc-700 dark:text-zinc-300">
                I accept the{' '}
                <a
                  href={marketingUrl('/terms')}
                  className="text-sky-600 dark:text-sky-400 hover:text-sky-700 dark:hover:text-sky-300"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms of Service
                </a>{' '}
                and{' '}
                <a
                  href={marketingUrl('/privacy')}
                  className="text-sky-600 dark:text-sky-400 hover:text-sky-700 dark:hover:text-sky-300"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </a>
              </label>
            </div>
          </div>
        </div>

        <div className="mt-6">
          <button
            type="submit"
            disabled={!isHealthy || isSubmitting || !termsAccepted}
            className="w-full px-4 py-2.5 bg-sky-600 hover:bg-sky-700 disabled:bg-zinc-400 dark:disabled:bg-zinc-600 text-white font-medium rounded-lg transition-colors disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="signup-submit-button"
          >
            {(() => {
              if (isSubmitting) {
                return (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Creating account...</span>
                  </>
                );
              }
              if (!isHealthy) {
                return 'System Unavailable';
              }
              return 'Continue';
            })()}
          </button>
        </div>
      </div>
      <div className="px-6 pb-6 pt-0">
        <div className="text-center">
          <span className="text-sm text-secondary">Already have an account? </span>
          <Link
            to={APP_ROUTES.LOGIN}
            className="text-sm text-sky-600 dark:text-sky-400 hover:text-sky-700 dark:hover:text-sky-300"
            data-testid="signup-login-link"
          >
            Sign In
          </Link>
        </div>
      </div>
    </form>
  );
}

interface AwaitingVerificationProps {
  email: string;
  otpCode: string;
  onOtpChange: (value: string) => void;
  verifying: boolean;
  verifyError: string | null;
  resending: boolean;
  resendSuccess: boolean;
  resendError: string | null;
  canResend: boolean;
  secondsRemaining: number;
  onSubmit: (e: React.FormEvent) => void;
  onResend: () => void;
  onUseDifferentEmail: () => void;
}

function AwaitingVerification({
  email,
  otpCode,
  onOtpChange,
  verifying,
  verifyError,
  resending,
  resendSuccess,
  resendError,
  canResend,
  secondsRemaining,
  onSubmit,
  onResend,
  onUseDifferentEmail,
}: AwaitingVerificationProps) {
  return (
    <div className="p-6" data-testid="signup-awaiting-verification">
      <div className="flex justify-center mb-4">
        <div className="h-16 w-16 bg-sky-100 dark:bg-sky-900/30 rounded-full flex items-center justify-center">
          <Mail className="h-8 w-8 text-sky-600 dark:text-sky-400" />
        </div>
      </div>

      <h3 className="text-xl font-semibold text-center mb-2 text-primary">Check your email</h3>
      <p className="text-sm text-center text-secondary mb-6">
        We sent a 6-digit code to{' '}
        <span className="font-medium text-primary" data-testid="signup-verification-email">
          {email}
        </span>
      </p>

      <form onSubmit={onSubmit} className="space-y-4" data-testid="signup-otp-form">
        {verifyError && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-600 dark:text-red-400">{verifyError}</p>
          </div>
        )}

        <div>
          <label
            htmlFor="signupOtpCode"
            className="text-xs text-zinc-600 dark:text-zinc-400 mb-1 block"
          >
            Enter the 6-digit code from your email:
          </label>
          <input
            id="signupOtpCode"
            type="text"
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            value={otpCode}
            onChange={(e) => onOtpChange(e.target.value)}
            placeholder="123456"
            className="w-full px-4 py-3 text-center text-2xl font-mono tracking-widest border-2 border-zinc-300 dark:border-zinc-600 rounded-lg bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:border-sky-500 focus:ring-2 focus:ring-sky-200 dark:focus:ring-sky-800"
            disabled={verifying}
            autoComplete="one-time-code"
            autoFocus
            data-testid="signup-otp-input"
          />
        </div>

        <button
          type="submit"
          disabled={verifying || otpCode.length !== 6}
          className="w-full px-4 py-2.5 bg-sky-600 hover:bg-sky-700 disabled:bg-zinc-400 dark:disabled:bg-zinc-600 text-white font-medium rounded-lg transition-colors disabled:cursor-not-allowed flex items-center justify-center gap-2"
          data-testid="signup-otp-submit-button"
        >
          {verifying ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Verifying...</span>
            </>
          ) : (
            <span>Verify</span>
          )}
        </button>

        {resendSuccess && (
          <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <p className="text-sm text-green-700 dark:text-green-400">
              Verification email sent! Check your inbox.
            </p>
          </div>
        )}

        {resendError && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-600 dark:text-red-400">{resendError}</p>
          </div>
        )}

        <button
          type="button"
          onClick={onResend}
          disabled={resending || !canResend}
          className="w-full px-4 py-2 bg-zinc-100 dark:bg-zinc-700 hover:bg-zinc-200 dark:hover:bg-zinc-600 disabled:bg-zinc-50 dark:disabled:bg-zinc-800 text-zinc-700 dark:text-zinc-300 disabled:text-zinc-400 dark:disabled:text-zinc-500 font-medium rounded-lg transition-colors disabled:cursor-not-allowed flex items-center justify-center gap-2"
          data-testid="signup-resend-button"
        >
          {(() => {
            if (resending) {
              return (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  <span>Sending...</span>
                </>
              );
            }
            if (canResend) {
              return (
                <>
                  <RefreshCw className="h-4 w-4" />
                  <span>Resend code</span>
                </>
              );
            }
            return (
              <>
                <RefreshCw className="h-4 w-4" />
                <span>Resend in {secondsRemaining}s</span>
              </>
            );
          })()}
        </button>

        <button
          type="button"
          onClick={onUseDifferentEmail}
          className="w-full text-sm text-sky-600 dark:text-sky-400 hover:text-sky-700 dark:hover:text-sky-300 py-2 flex items-center justify-center gap-1"
          data-testid="signup-use-different-email-button"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Use a different email</span>
        </button>
      </form>
    </div>
  );
}

export default Signup;
